import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const SERVICES = [
  { key: "24-hour-monitoring", labelKey: "24-hour-monitoring", icon: "/icons/24hourMonitoring.svg" },
  { key: "professional-care", labelKey: "professional-care", icon: "/icons/profCare.svg" },
  { key: "flexible-pet-transport", labelKey: "flexible-pet-transport", icon: "/icons/flexTransport.svg" },
  { key: "daily-updates", labelKey: "daily-updates", icon: "/icons/dailyUpdates.svg" },
  { key: "temperature-control", labelKey: "temperature-control", icon: "/icons/tempControl.svg" },
  { key: "daily-cleaning", labelKey: "daily-cleaning", icon: "/icons/dailyCleaning.svg" },
];

const HotelAdd_Information_2 = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { hotelData, loading, saveStep, fetchDraft } = useHotel();
  const [selectedServices, setSelectedServices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id && !hotelData.services) {
      fetchDraft(id);
    }
  }, [id, hotelData.services, fetchDraft]);

  useEffect(() => {
    if (hotelData.services) {
      // Handle both nested and flat structures
      const servicess = Array.isArray(hotelData.services)
        ? hotelData.services
        : hotelData.services.services || [];
      setSelectedServices(servicess);
    }
  }, [hotelData.services]);

  const toggleServices = (key) => {
    setSelectedServices((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      await saveStep("services", selectedServices);
      navigate(`/hotel/add/info3/${id}`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      await saveStep("services", selectedServices);
      navigate(`/hotel/onboarding`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } 
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto", }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index === 0 ? "#31BACD" : "transparent",
                    border: index === 0 ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index === 0 ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === 0 ? "600" : "400",
                    color: index === 0 ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          {/* Heading */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", mb: 1, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "20px" : "24px",
                  color: "#000",
                  mb: 1,
                }}
              >
                {t('HotelInfoStep2Title')}
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "13px" : "14px",
                  color: "#666",
                  maxWidth: "600px",
                }}
              >
                {t('HotelInfoStep2Desc')}
              </Typography>
            </Box>
          </Box>

          {/* Service type grid */}
          {loading ? (
            <LoadingGif />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                gap: 2,
                width: "100%",
                mt: 4,
              }}
            >
              {SERVICES.map((services) => {
                const selected = selectedServices.includes(services.key);
                return (
                  <Box
                    key={services.key}
                    onClick={() => toggleServices(services.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      backgroundColor: "#FFFFFF",
                      border: selected ? "2px solid #31BACD" : "2px solid #D1D1D1",
                      borderRadius: "10px",
                      padding: "20px",
                      height: isMobile ? "70px" : "90px",
                      cursor: "pointer",
                      transition: "border-color 0.2s ease",
                    }}
                  >
                    <img src={services.icon} alt={t(services.labelKey)} style={{ width: "32px", height: "32px" }} />
                    <Typography sx={{ fontSize: isMobile ? "14px" : "16px", fontWeight: "500", color: "#000" }}>
                      {t(services.labelKey)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={selectedServices.length === 0 || loading}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/info1/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={selectedServices.length === 0 || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_Information_2;

